'use client';

import { User } from 'common/interfaces/api';
import { isHavingPrimeBenefits } from 'common/utils/payment';
import { Button } from 'components/v2/atoms/button';
import InfinityScroll from 'components/v2/molecules/infinity-scroll';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation } from 'react-query';
import {
  createForumComment,
  deleteForumComment,
  ForumCommentsResponseType,
  ForumCommentType,
  getForumComments,
} from 'service/forum';
import useGlobalStore from 'store/global';

import useStore from 'store/timeline';
import ForumCommentBox from './forum-comment-box';
import ForumCommentDetail from './forum-comment-detail';
import ForumCommentItem from './forum-comment-item';

interface ForumSectionPropsType {
  creator: User;
}

const ForumSection = ({ creator }: ForumSectionPropsType) => {
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const creatorId = creator.id;
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const router = useRouter();
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const currentUser = useStore((state) => state.currentUser);

  // Check if user has prime benefits for this creator
  const hasSubscription = isHavingPrimeBenefits(creator);
  const isCreatorMySelf = !!currentUser?.id && currentUser?.id === creatorId;
  const haveForumPermission = hasSubscription || isCreatorMySelf;

  // Check for comment detail in URL
  const commentId = router.query.comment_id
    ? Number(router.query.comment_id)
    : null;

  // Get next page param for infinite query (cursor-based)
  const getNextPageParam = useCallback(
    (lastPage: ForumCommentsResponseType) => {
      const comments = lastPage?.data;
      if (comments && comments.length > 0) {
        const lastComment = comments[comments.length - 1];
        return lastComment.id;
      }
      return undefined;
    },
    [],
  );

  // Fetch forum comments
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery<ForumCommentsResponseType>(
    ['forum-comments', creatorId?.toString()],
    async ({ pageParam }) => {
      return getForumComments(creatorId, pageParam as number | undefined);
    },
    {
      getNextPageParam,
      enabled:
        !!creatorId &&
        !commentId &&
        isAuthInit &&
        !!currentAuth &&
        !!haveForumPermission,
      refetchOnWindowFocus: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    },
  );

  const comments: ForumCommentType[] = useMemo(
    () =>
      data?.pages
        ? data.pages.reduce<ForumCommentType[]>((curr, page) => {
            const items = page.data || [];
            return [...curr, ...items];
          }, [])
        : [],
    [data?.pages],
  );

  // Create comment mutation
  const { mutateAsync: createComment } = useMutation(async (text: string) => {
    return createForumComment(creatorId, text);
  });

  // Delete comment mutation (list view only)
  const { mutate: deleteComment } = useMutation(
    async (targetCommentId: number) => {
      await deleteForumComment(targetCommentId);
    },
    {
      onSuccess: () => {
        void refetch();
        setSnackbarMessage({ type: 'success', text: 'コメントを削除しました' });
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'コメントの削除に失敗しました',
        });
      },
    },
  );

  const handleCommentSubmit = async (text: string) => {
    await createComment(text);
    void refetch();
    setSnackbarMessage({ type: 'success', text: 'コメントを投稿しました' });
  };

  const handleDelete = (targetCommentId: number) => {
    deleteComment(targetCommentId);
  };

  const handleSubscribe = () => {
    if (!creator) return;
    if (creator.is_open_for_subscription === false) {
      setSnackbarMessage({
        type: 'error',
        text: 'このクリエイターは現在サブスクリプションを受け付けていません',
      });
      return;
    }
    showPaymentDialog({ creator });
  };

  // Clear comment detail from URL
  const handleBackToList = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { comment_id: _, ...restQuery } = router.query;
    void router.replace(
      { pathname: router.pathname, query: restQuery },
      undefined,
      { shallow: true },
    );
  };

  // Show comment detail view when commentId exists
  if (commentId) {
    return (
      <ForumCommentDetail
        commentId={commentId}
        creator={creator}
        onBack={handleBackToList}
      />
    );
  }

  // Show subscription prompt when not having forum permission
  if (!haveForumPermission) {
    return (
      <div className="space-y-4">
        <div className="relative rounded-xl bg-white p-6 text-center shadow-sm">
          <div className="mb-3 text-4xl">🎉</div>
          <h2 className="mb-3 text-lg font-bold text-pp-text-primary">
            プライム会員専用掲示板へようこそ！
          </h2>
          <p className="mx-auto mb-4 max-w-md text-sm leading-relaxed text-pp-text-secondary">
            ここはプライム登録者のみが参加できる、特別なコミュニティスペースです。
            日々のマーケットを深く読み解き、メンバー同士で知見を共有しましょう。
            <br />
            独自の視点やマーケットに関する分析など、あなたの積極的なコメントをお待ちしています！
          </p>
          <Button
            variant="defaultGreen"
            size="md"
            onClick={handleSubscribe}
            className="rounded-full px-6"
          >
            掲示板に参加する
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!isLoading && comments.length === 0) {
    return (
      <div className="space-y-4">
        <ForumCommentBox onSubmit={handleCommentSubmit} />

        <div className="flex min-h-[250px] w-full flex-col items-center justify-center rounded-lg bg-white p-4 text-center shadow-sm">
          <Image
            src="/images/icons/empty.svg"
            alt="empty"
            width={100}
            height={100}
          />
          <p className="mt-4 text-base font-semibold text-gray-600">
            まだコメントがありません。
            <br />
            最初のコメントを投稿してみましょう！
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Comment input box */}
      <ForumCommentBox onSubmit={handleCommentSubmit} />

      {/* Comments list with infinity scroll */}
      <InfinityScroll
        fetchNextPage={fetchNextPage}
        isLoading={isFetchingNextPage}
        hasNextPage={hasNextPage ?? false}
        isError={isError}
        className="!h-auto !overflow-visible"
      >
        <div className="space-y-3">
          {comments.map((comment) => (
            <ForumCommentItem
              key={comment.id}
              comment={comment}
              profileUserId={creatorId}
              profileUsername={creator.username}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </InfinityScroll>

      {/* Loading state for initial load */}
      {isLoading && (
        <div className="py-8 text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-4 border-gray-200 border-t-pp-green-primary"></div>
        </div>
      )}
    </div>
  );
};

export default ForumSection;
